import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  getDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  increment,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

export interface FavoriteProfessional {
  professionalId: string;
  createdAt: Timestamp;
  notes?: string;
}

/**
 * Add a professional to user's favorites
 */
export const addFavorite = async (
  userId: string,
  professionalId: string
): Promise<void> => {
  try {
    // Add to user's favorites subcollection - only store ID and timestamp
    const favoriteRef = doc(db, `favorites/${userId}/savedProfessionals/${professionalId}`);
    await setDoc(favoriteRef, {
      professionalId,
      createdAt: serverTimestamp()
    });

    // Increment the professional's favorite count
    const professionalRef = doc(db, `users/${professionalId}`);
    await updateDoc(professionalRef, {
      favoriteCount: increment(1)
    });

    console.log('✅ Added favorite:', professionalId);
  } catch (error) {
    console.error('Error adding favorite:', error);
    throw error;
  }
};

/**
 * Remove a professional from user's favorites
 */
export const removeFavorite = async (
  userId: string,
  professionalId: string
): Promise<void> => {
  try {
    // Remove from user's favorites subcollection
    const favoriteRef = doc(db, `favorites/${userId}/savedProfessionals/${professionalId}`);
    await deleteDoc(favoriteRef);

    // Decrement the professional's favorite count
    const professionalRef = doc(db, `users/${professionalId}`);
    await updateDoc(professionalRef, {
      favoriteCount: increment(-1)
    });

    console.log('✅ Removed favorite:', professionalId);
  } catch (error) {
    console.error('Error removing favorite:', error);
    throw error;
  }
};

/**
 * Check if a professional is favorited by the user
 */
export const isFavorited = async (
  userId: string,
  professionalId: string
): Promise<boolean> => {
  try {
    const favoriteRef = doc(db, `favorites/${userId}/savedProfessionals/${professionalId}`);
    const favoriteDoc = await getDoc(favoriteRef);
    return favoriteDoc.exists();
  } catch (error) {
    console.error('Error checking favorite status:', error);
    return false;
  }
};

/**
 * Get all favorites for a user
 */
export const getUserFavorites = async (userId: string): Promise<FavoriteProfessional[]> => {
  try {
    const favoritesRef = collection(db, `favorites/${userId}/savedProfessionals`);
    const q = query(favoritesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      createdAt: doc.data().createdAt as Timestamp
    } as FavoriteProfessional));
  } catch (error) {
    console.error('Error getting favorites:', error);
    return [];
  }
};

/**
 * Subscribe to real-time updates for a specific favorite
 */
export const subscribeFavoriteStatus = (
  userId: string,
  professionalId: string,
  callback: (isFavorited: boolean) => void
): (() => void) => {
  const favoriteRef = doc(db, `favorites/${userId}/savedProfessionals/${professionalId}`);

  const unsubscribe = onSnapshot(
    favoriteRef,
    (doc) => {
      callback(doc.exists());
    },
    (error) => {
      console.error('Error subscribing to favorite status:', error);
      callback(false);
    }
  );

  return unsubscribe;
};

/**
 * Subscribe to real-time updates for all user favorites
 */
export const subscribeUserFavorites = (
  userId: string,
  callback: (favorites: FavoriteProfessional[]) => void
): (() => void) => {
  const favoritesRef = collection(db, `favorites/${userId}/savedProfessionals`);
  const q = query(favoritesRef, orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const favorites = snapshot.docs.map(doc => ({
        ...doc.data(),
        createdAt: doc.data().createdAt as Timestamp
      } as FavoriteProfessional));
      callback(favorites);
    },
    (error) => {
      console.error('Error subscribing to favorites:', error);
      callback([]);
    }
  );

  return unsubscribe;
};

/**
 * Toggle favorite status (add if not favorited, remove if favorited)
 */
export const toggleFavorite = async (
  userId: string,
  professionalId: string
): Promise<boolean> => {
  const favorited = await isFavorited(userId, professionalId);

  if (favorited) {
    await removeFavorite(userId, professionalId);
    return false;
  } else {
    await addFavorite(userId, professionalId);
    return true;
  }
};
